import { useTheme } from "@mui/material";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { MarkPlot } from "@mui/x-charts/LineChart";

interface SavingsDataPoint {
  value: number;
  label: string;
}

interface SavingsSparklineProps {
  dataPoints: SavingsDataPoint[];
  width?: number;
  height?: number;
}

export const SavingsSparkline = ({
  dataPoints,
  width = 80,
  height = 32,
}: SavingsSparklineProps) => {
  const theme = useTheme();

  if (dataPoints.length < 2) {
    return null;
  }

  const values = dataPoints.map((dp) => dp.value);
  const labels = dataPoints.map((dp) => dp.label);

  // Color based on whether the current (last) value is positive or negative
  const lastValue = values[values.length - 1] ?? 0;
  const color =
    lastValue >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <SparkLineChart
      data={values}
      xAxis={{
        scaleType: "point",
        data: labels,
      }}
      width={width}
      height={height}
      curve="linear"
      area
      showTooltip
      valueFormatter={(value) =>
        value != null ? `$${value.toFixed(2)}/t` : ""
      }
      slotProps={{
        tooltip: {
          sx: {
            "& .MuiChartsTooltip-mark": {
              borderColor: color,
              backgroundColor: color,
            },
          },
        },
      }}
      sx={{
        "& .MuiLineElement-root": {
          stroke: color,
        },
        "& .MuiAreaElement-root": {
          fill: color,
          fillOpacity: 0.4,
        },
        "& .MuiMarkElement-root": {
          display: "none",
        },
        "& .MuiMarkElement-root:last-of-type": {
          display: "block",
          fill: color,
          stroke: color,
          r: 4,
          transform: "translateX(-4px)",
        },
        "& .MuiHighlightElement-root": {
          fill: color,
          stroke: color,
        },
      }}
    >
      <MarkPlot />
    </SparkLineChart>
  );
};
